<template>
  <ClientOnly>
    <LibLocaleVersionOverlay
      v-if="mountOverlay"
      :detected-locale="geoInformationStore.detectedLocale"
      :display-language="geoInformationStore.clientLocale"
    />
  </ClientOnly>
</template>
<script lang="ts" setup>
import { useGeoinformationStore } from '~/stores/geoinformationStore/geoinformationStore';
import { countryLocaleMapping } from '~/utils/countriesWithLocaleMapping';
import { convertContentfulToWebappLocaleCode } from '~/utils/convertContentfulToWebappLocaleCode';

const { $resolvedLocale, $modals } = useNuxtApp();
const geoInformationStore = useGeoinformationStore();

const mountOverlay = computed(() => {
  const {
    public: { isPreviewEnabled }
  } = useRuntimeConfig();

  // Don't show overlay in preview mode (internal use only)
  if (isPreviewEnabled) {
    return false;
  }

  // We do not want to show multiple modals for now.
  // LocaleVersionOverlay is for now the least important modal.
  // Multiple modals might be possible via the RegionSelector and a
  // change of the prepared locale triggering this computed.
  // @see https://gcp.baslerweb.com/jira/browse/DBP-117
  // XXX: we might want to use a global hierarchy for modals.
  if ($modals.regionSelectorOpen.value) {
    return false;
  }

  if (
    !geoInformationStore.clientLocale ||
    !geoInformationStore.detectedLocale
  ) {
    // can only show overlay if geo information is present
    return false;
  }

  // Don't show overlay if user is already on their detected locale's page
  if (
    geoInformationStore.detectedLocale?.toLowerCase() ===
    $resolvedLocale.value?.toLowerCase()
  ) {
    return false;
  }

  // Don't show overlay if current locale is valid for detected country
  // This prevents overlay when user chooses a different but valid locale for their country
  // e.g., user in Germany chooses English (en) - this is valid, so no overlay needed
  const currentLocaleMapping = countryLocaleMapping.find(
    (mapping) => mapping.locale === $resolvedLocale.value
  );
  if (
    currentLocaleMapping &&
    geoInformationStore.detectedCountry &&
    currentLocaleMapping.identifier.includes(
      geoInformationStore.detectedCountry
    )
  ) {
    return false;
  }

  // Don't show overlay if user has actively selected the current locale
  // If preferredLocale matches the current locale, respect the user's choice
  // But still show overlay if user navigates to a different locale
  //
  // IMPORTANT: We need to normalize locale formats for comparison:
  // - preferredLocale is stored in webapp format (e.g., "zh-tw")
  // - currentLocale ($resolvedLocale) is in contentful format (e.g., "zh-Hant-TW")
  const normalizedCurrentLocale = convertContentfulToWebappLocaleCode(
    $resolvedLocale.value || ''
  );
  const normalizedPreferredLocale =
    geoInformationStore.preferredLocale?.toLowerCase();
  if (
    geoInformationStore.preferredLocale &&
    normalizedPreferredLocale === normalizedCurrentLocale
  ) {
    return false;
  }

  return true;
});
</script>
